'use client';

import React, { useState, useEffect } from 'react';
import Carousel from 'react-multi-carousel';
import Image from 'next/image';
import { Button } from '@carbon/react';
import styles from './BannerSection.module.scss';
import { BannerItem } from '@/app/types';
import ArrowRight from '@carbon/icons-react/es/ArrowRight.js';

interface Props {
  banners: BannerItem[];
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Error in BannerSlider:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return <div role="alert">Something went wrong with the banner slider.</div>;
    }
    return this.props.children;
  }
}

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
    slidesToSlide: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 768 },
    items: 1,
    slidesToSlide: 1,
  },
  mobile: {
    breakpoint: { max: 768, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
};

const BannerSliderClient: React.FC<Props> = ({ banners }) => {
  const [isCarouselReady, setIsCarouselReady] = useState(false);

  useEffect(() => {
    // Defer carousel initialization to after initial paint
    const timer = setTimeout(() => {
      setIsCarouselReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  if (!banners || banners.length === 0) return null;

  const sortedBanners = [...banners].sort(
    (a, b) => Number(a.order_no || 0) - Number(b.order_no || 0),
  );

  const firstBanner = sortedBanners[0];

  // Render first banner immediately for LCP, then show carousel
  if (!isCarouselReady && firstBanner) {
    return (
      <div className={styles.banner_inner} role="region" aria-labelledby="section-title">
        <h2 id="section-title" className={styles.visually_hidden}>
          Featured Banners
        </h2>
        <div className={styles.slide}>
          <div className={styles.contentWrapper}>
            <div className={styles.textSection}>
              <h1>{firstBanner.title}</h1>
              <p>{firstBanner.description}</p>
              {firstBanner.actionButtons && firstBanner.actionButtons.length > 0 && (
                <div className={styles.buttonGroup}>
                  {firstBanner.actionButtons.map((btn, btnIndex) => (
                    <Button
                      key={btnIndex}
                      href={btn.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      kind={btnIndex % 2 === 0 ? 'primary' : 'tertiary'}
                      className={styles.bannerButton}
                    >
                      <span className={styles.buttonContent}>
                        {btn.text}
                        <ArrowRight className={styles.buttonIcon} size={16} />
                      </span>
                    </Button>
                  ))}
                </div>
              )}
            </div>

            <div className={`${styles.imageSection} ${styles.hideOnMobile}`}>
              <Image
                src={firstBanner.imageUrl}
                alt={firstBanner.title}
                width={600}
                height={400}
                priority
                loading="eager"
                quality={90}
                fetchPriority="high"
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 600px"
                decoding="sync"
                placeholder="blur"
                blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2YwZjBmMCIvPjwvc3ZnPg=="
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.banner_inner} role="region" aria-labelledby="section-title">
      <h2 id="section-title" className={styles.visually_hidden}>
        Featured Banners
      </h2>

      <ErrorBoundary>
        <Carousel
          responsive={responsive}
          infinite={true}
          // Autoplay configuration: slides automatically advance every 4 seconds
          // Set to false to disable autoplay, or adjust autoPlaySpeed for different timing
          autoPlay={true}
          autoPlaySpeed={4000} // Time in milliseconds between slide transitions
          transitionDuration={500}
          keyBoardControl={true}
          showDots={true}
          removeArrowOnDeviceType={['mobile']}
          containerClass="carousel-container"
          itemClass="carousel-item"
          dotListClass="custom-dot-list-style"
          pauseOnHover={true}
          shouldResetAutoplay={true}
          rewind={false}
          rewindWithAnimation={false}
        >
          {sortedBanners.map((banner, index) => (
            <div className={styles.slide} key={index}>
              <div className={styles.contentWrapper}>
                <div className={styles.textSection}>
                  <h1>{banner.title}</h1>
                  <p>{banner.description}</p>
                  {banner.actionButtons && banner.actionButtons.length > 0 && (
                    <div className={styles.buttonGroup}>
                      {banner.actionButtons.map((btn, btnIndex) => (
                        <Button
                          key={btnIndex}
                          href={btn.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          kind={btnIndex % 2 === 0 ? 'primary' : 'tertiary'}
                          className={styles.bannerButton}
                        >
                          <span className={styles.buttonContent}>
                            {btn.text}
                            <ArrowRight className={styles.buttonIcon} size={16} />
                          </span>
                        </Button>
                      ))}
                    </div>
                  )}
                </div>

                <div className={`${styles.imageSection} ${styles.hideOnMobile}`}>
                  <Image
                    src={banner.imageUrl}
                    alt={banner.title}
                    width={600}
                    height={400}
                    priority={index === 0}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    quality={index === 0 ? 90 : 80}
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 600px"
                    decoding={index === 0 ? 'sync' : 'async'}
                    placeholder="blur"
                    blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2YwZjBmMCIvPjwvc3ZnPg=="
                  />
                </div>
              </div>
            </div>
          ))}
        </Carousel>
      </ErrorBoundary>
    </div>
  );
};

export default BannerSliderClient;
